import { ALL_ORGS_SELECTED } from 'lib/constants'

import React, { createContext, useCallback, useContext, useRef, useState, useEffect, ReactNode } from 'react'
import { CurrentUserV5, Organization, OrgType, User } from 'lib/types'
import {
  getAllOrganizations,
  getOrganizationsV5,
  getAllOrganizationsNames,
  getOrganization,
  getCurrentUser,
  getCurrentUserV5,
} from 'models/api'
import { ReportStatus } from 'lib/types/reports'
import { getRequestV5 } from 'models/api/rest'
import { NavItems } from 'lib/types/navItems'
import { AlertSessions, ChildOrgMap } from 'models'
import { isSuperAdmin, isVoiceEnabled } from 'lib/utils/auth'
import { resolveVoiceOrgId } from 'lib/utils/voice'
import { subscribeToOrgs, updateActiveSubscriptions } from 'models/AlertSessions'

import type { License } from 'lib/types/license'
import { getLicenses } from 'models/api/licenses'

type GlobalContextType = {
  loading: boolean
  orgs: Organization[]
  navItems: NavItems[]
  orgSearchBar: string | undefined
  selectedOrg: Organization | null
  selectedOrgId: string | null
  reportStatus: ReportStatus | null
  refreshFlag: boolean
  // Session-only: when a superadmin flips this on in the org details page, the
  // per-device "Single Press Activation" toggles become visible. Never persisted.
  singlePressActivationEnabled: boolean
  currentUser: User | null
  currentUserV5: CurrentUserV5 | null

  licenses: License[]
  licensesLoading: boolean
  licensesError: string | null
  reloadLicenses: () => Promise<void>

  // Changes the sidebar org (persisted). `selectedOrg` follows via the sync effect.
  selectOrg: (orgId: string) => void
  // The org an alert's voice channel runs on; null when nothing up its chain has voice.
  findVoiceOrgId: (orgId: string | null | undefined) => Promise<string | null>

  setOrgs: React.Dispatch<React.SetStateAction<Organization[]>>
  setNavItems: React.Dispatch<React.SetStateAction<NavItems[]>>
  setOrgSearchBar: React.Dispatch<React.SetStateAction<string | undefined>>
  setSelectedOrg: React.Dispatch<React.SetStateAction<Organization | null>>
  setSelectedOrgId: React.Dispatch<React.SetStateAction<string | null>>
  setReportStatus: React.Dispatch<React.SetStateAction<ReportStatus | null>>
  setRefreshFlag: React.Dispatch<React.SetStateAction<boolean>>
  setSinglePressActivationEnabled: React.Dispatch<React.SetStateAction<boolean>>
  setCurrentUser: React.Dispatch<React.SetStateAction<User | null>>
  setCurrentUserV5: React.Dispatch<React.SetStateAction<CurrentUserV5 | null>>
}

const GlobalContext = createContext<GlobalContextType>({
  loading: true,
  orgs: [],
  navItems: [],
  orgSearchBar: undefined,
  selectedOrg: null,
  selectedOrgId: null,
  reportStatus: null,
  refreshFlag: false,
  singlePressActivationEnabled: false,
  currentUser: null,
  currentUserV5: null,

  licenses: [],
  licensesLoading: false,
  licensesError: null,
  reloadLicenses: async () => {},

  selectOrg: () => {},
  findVoiceOrgId: async () => null,

  setOrgs: () => {},
  setNavItems: () => {},
  setOrgSearchBar: () => {},
  setSelectedOrg: () => {},
  setSelectedOrgId: () => {},
  setReportStatus: () => {},
  setRefreshFlag: () => {},
  setSinglePressActivationEnabled: () => {},
  setCurrentUser: () => {},
  setCurrentUserV5: () => {},
})

type GlobalProviderProps = {
  children: ReactNode
}

export const useGlobal = () => {
  const context = useContext(GlobalContext)
  if (!context) {
    throw new Error('useBeacons must be used within a BeaconsProvider')
  }
  return context
}

export const GlobalProvider: React.FC<GlobalProviderProps> = ({ children }) => {
  const [loading, setLoading] = useState(true)
  const [orgs, setOrgs] = useState<Organization[]>([])
  const [navItems, setNavItems] = useState<NavItems[]>([])
  const [orgSearchBar, setOrgSearchBar] = useState<string | undefined>(undefined)
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null)
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)
  const [reportStatus, setReportStatus] = useState<ReportStatus | null>(null)
  const [refreshFlag, setRefreshFlag] = useState<boolean>(false)
  const [singlePressActivationEnabled, setSinglePressActivationEnabled] = useState<boolean>(false)
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [currentUserV5, setCurrentUserV5] = useState<CurrentUserV5 | null>(null)
  const alertSessionsLegacy = AlertSessions.use().alertSessions

  const [licenses, setLicenses] = useState<License[]>([])
  const [licensesLoading, setLicensesLoading] = useState(false)
  const [licensesError, setLicensesError] = useState<string | null>(null)

  const isVoiceOrg = (org: Organization | null) => typeof org?.zelloUserMax === 'number'

  // State and localStorage change together so a refresh can't restore a stale
  // org. The fetch of `selectedOrg` is left to the sync effect below, which
  // drops responses for an org that is no longer selected.
  const selectOrg = useCallback((orgId: string) => {
    const id = orgId || ALL_ORGS_SELECTED
    localStorage.setItem('selected_org_id', id)
    setSelectedOrgId(id)
    if (id === ALL_ORGS_SELECTED) setSelectedOrg(null)
  }, [])

  // `orgs` comes from the same summary endpoint as `selectedOrg`, so it carries
  // the Zello and subscription fields `isVoiceEnabled` reads; anything missing
  // from it (a parent group outside the list) is fetched.
  const orgsRef = useRef<Organization[]>([])
  orgsRef.current = orgs
  const findVoiceOrgId = useCallback(
    (orgId: string | null | undefined) =>
      resolveVoiceOrgId(
        orgId,
        async id => orgsRef.current.find(o => o.id === id) || getOrganization(id),
        ChildOrgMap.store.getState().parentOrgMap,
      ),
    [],
  )

  // Voice needs a single org selected, so a user whose access is one
  // voice-enabled group starts on it rather than on All Properties. First load
  // only: the effect below reruns on `refreshFlag`, and must not undo an
  // All Properties choice made since.
  const defaultedOffAll = useRef(false)

  const reloadLicenses = async () => {
    const orgId = selectedOrgId

    if (!orgId || orgId === ALL_ORGS_SELECTED) {
      setLicenses([])
      setLicensesError(null)
      setLicensesLoading(false)
      return
    }

    if (!isVoiceOrg(selectedOrg)) {
      setLicenses([])
      setLicensesError(null)
      setLicensesLoading(false)
      return
    }

    setLicensesLoading(true)
    setLicensesError(null)

    try {
      const list = await getLicenses(orgId)
      setLicenses(list)
    } catch (e: any) {
      setLicenses([])
      setLicensesError(e?.message ?? 'Failed to load licenses')
    } finally {
      setLicensesLoading(false)
    }
  }

  useEffect(() => {
    const lsOrgId = localStorage.getItem('selected_org_id')
    setSelectedOrgId(lsOrgId)
    let orgIsSet = false

    getCurrentUser().then(u => setCurrentUser(u))
    getCurrentUserV5().then(u => setCurrentUserV5(u))

    getOrganizationsV5()
      .then(data => {
        setOrgs(data)

        if (data.length === 1) {
          localStorage.setItem('selected_org_id', data[0].id)
          setSelectedOrg(data[0])
          setSelectedOrgId(data[0].id)
          orgIsSet = true
        } else if (!defaultedOffAll.current && (!lsOrgId || lsOrgId === ALL_ORGS_SELECTED) && !isSuperAdmin()) {
          const voiceGroups = data.filter(o => o.orgType === OrgType.Group && isVoiceEnabled(o))
          if (voiceGroups.length === 1) {
            selectOrg(voiceGroups[0].id)
            orgIsSet = true
          }
        }
        defaultedOffAll.current = true

        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })

    if (lsOrgId && lsOrgId !== ALL_ORGS_SELECTED && !orgIsSet) {
      getOrganization(lsOrgId)
        .then(data => {
          setSelectedOrg(data)
          setLoading(false)
        })
        .catch(() => {
          setLoading(false)
        })
    }

    getRequestV5({
      endpoint: '/reports/status',
      headers: lsOrgId === null || lsOrgId === ALL_ORGS_SELECTED ? {} : { org_id: lsOrgId },
      handleSuspendedOrg: true,
      params: {},
    })
      .then(response => {
        setReportStatus(response.data.data)
      })
      .catch(error => {
        if (error?.message !== 'Network Error') {
          console.error('Failed to fetch report status:', error)
        } else {
          console.warn('Network Error fetching /reports/status (suppressed)')
        }
      })

    getAllOrganizationsNames().then(data => setNavItems(data))
    setRefreshFlag(false)
  }, [refreshFlag, selectOrg])

  const isInitialRender = useRef(true)
  useEffect(() => {
    if (!isInitialRender.current) {
      getOrganizationsV5(orgSearchBar)
        .then(data => {
          setOrgs(data)

          const lsOrgId = localStorage.getItem('selected_org_id')
          if (lsOrgId && lsOrgId !== ALL_ORGS_SELECTED) {
            getOrganization(lsOrgId).then(data => {
              setSelectedOrg(data)
            })
          }

          setLoading(false)
        })
        .catch(() => {
          setLoading(false)
        })
    }
    isInitialRender.current = false
  }, [alertSessionsLegacy, orgSearchBar])

  useEffect(() => {
    getAllOrganizations().then(orgs => {
      updateActiveSubscriptions(subscribeToOrgs(orgs))
      return orgs
    })
  }, [])

  // Keep selectedOrg synchronized with selectedOrgId for multi-org users.
  useEffect(() => {
    if (!selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED) {
      setSelectedOrg(null)
      return
    }

    if (selectedOrg?.id === selectedOrgId) return

    let alive = true

    getOrganization(selectedOrgId)
      .then(org => {
        if (!alive) return
        setSelectedOrg(org)
      })
      .catch(err => {
        if (!alive) return
        console.error('Failed to sync selectedOrg from selectedOrgId:', err)
        setSelectedOrg(null)
      })

    return () => {
      alive = false
    }
  }, [selectedOrgId, selectedOrg?.id])

  useEffect(() => {
    reloadLicenses()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId, selectedOrg?.id])

  // Re-fetch the sidebar's status counts whenever the globally-selected org changes,
  // regardless of *how* it changed (autocomplete, programmatic set from admin/groups tab, etc).
  // Skip the very first fire; the initial mount fetch inside the refreshFlag effect above
  // already handles that case using the localStorage-hydrated org id.
  const reportStatusInitialized = useRef(false)
  useEffect(() => {
    if (!reportStatusInitialized.current) {
      reportStatusInitialized.current = true
      return
    }
    const scoped = selectedOrgId && selectedOrgId !== ALL_ORGS_SELECTED
    getRequestV5({
      endpoint: '/reports/status',
      headers: scoped ? { org_id: selectedOrgId } : {},
      handleSuspendedOrg: true,
      params: {},
    })
      .then(response => setReportStatus(response.data.data))
      .catch(err => {
        if (err?.message !== 'Network Error') {
          console.error('Failed to fetch report status:', err)
        }
      })
  }, [selectedOrgId])

  return (
    <GlobalContext.Provider
      value={{
        loading,
        orgs,
        setOrgs,
        navItems,
        setNavItems,
        orgSearchBar,
        setOrgSearchBar,
        selectedOrg,
        setSelectedOrg,
        selectedOrgId,
        setSelectedOrgId,
        reportStatus,
        setReportStatus,
        refreshFlag,
        setRefreshFlag,
        singlePressActivationEnabled,
        setSinglePressActivationEnabled,
        currentUser,
        setCurrentUser,
        currentUserV5,
        setCurrentUserV5,

        licenses,
        licensesLoading,
        licensesError,
        reloadLicenses,

        selectOrg,
        findVoiceOrgId,
      }}
    >
      {children}
    </GlobalContext.Provider>
  )
}
